// Uploading several documents at once: each file goes to its slot by its name.
//
// Used by "Procesar el día" (`ProcesoDelDia.jsx`). Nothing here names a client (B-40's rule for this folder): a slot is
// described only by its key and the label the client's names file gives it, so Bantotal's
// `RepMovCaja_<fecha>_<usuario>.xls` finds `movimiento_caja` ("Movimientos de caja") through the
// words both share — `mov` and `caja` — and not through a list of known file names.
//
// A file is a match only when one slot wins outright. A tie or no shared word leaves the file
// unrecognised, for the person to place by hand: a wrong guess would send the wrong report under
// a slot's name, which the backend reads and the closing then disagrees about.

import { nombreDeFuente } from "./cierreNombres";

// Words too common to say anything about which document a file is.
const VACIAS = new Set(["de", "del", "la", "las", "el", "los", "por", "y", "en", "con", "para"]);
const MINIMO = 3;

// "RepMovCaja_20260830_jperez.xls" -> ["rep", "mov", "caja", "jperez"]. Digits split words and are
// dropped: they are dates and users, never the document.
export const palabras = (texto) =>
  String(texto || "")
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((p) => p.length >= MINIMO && !VACIAS.has(p));

// Two words are the same when one begins the other: `mov` / `movimiento`, `suc` / `sucursal`,
// `recaudo` / `recaudos`.
const coinciden = (a, b) => a.startsWith(b) || b.startsWith(a);

const puntaje = (delArchivo, casilla) => {
  const deLaCasilla = [...casilla.clave, ...casilla.etiqueta];
  const comunes = delArchivo.filter((p) => deLaCasilla.some((q) => coinciden(p, q))).length;
  // Among slots sharing as many words, the one whose own key the file covers best is the more
  // specific: "Saldo caja" is `saldo_caja`, not `saldo_caja_cierre`.
  const sobrantes = casilla.clave.filter((q) => !delArchivo.some((p) => coinciden(p, q))).length;
  return { comunes, sobrantes };
};

// The slot a file name belongs to, or null.
export const claveDeArchivo = (nombreArchivo, claves, nombres) => {
  const delArchivo = palabras(nombreArchivo);
  if (!delArchivo.length) return null;
  const puntajes = claves
    .map((clave) => ({
      clave,
      ...puntaje(delArchivo, { clave: palabras(clave), etiqueta: palabras(nombreDeFuente(nombres, clave)) }),
    }))
    .filter((p) => p.comunes > 0)
    .sort((a, b) => b.comunes - a.comunes || a.sobrantes - b.sobrantes);
  const [primero, segundo] = puntajes;
  if (!primero) return null;
  if (segundo && segundo.comunes === primero.comunes && segundo.sobrantes === primero.sobrantes) return null;
  return primero.clave;
};

// B-101 — whether a file's name says the business date: "15 SEPTIEMBRE", "15092026", "1509".
// What tells the Drive of the 15th from the Drive of the 16th dropped in the same folder.
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const mencionaFecha = (nombre, fecha) => {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha || ""));
  if (!partes) return false;
  const [, y, m, d] = partes;
  const texto = String(nombre || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const digitos = texto.replace(/[^0-9]+/g, " ");
  if (digitos.includes(`${d}${m}${y}`) || digitos.includes(`${y}${m}${d}`) || new RegExp(`(^| )${d}${m}( |$)`).test(digitos)) return true;
  const mes = MESES[Number(m) - 1];
  return new RegExp(`(^|[^0-9])0?${Number(d)}[^0-9a-z]+(de )?${mes.slice(0, 3)}`).test(texto);
};

// Several files onto the slots. A slot receives one file: when two match it, the one whose name
// says the business date stays (B-101), else the most recently modified, and the other is reported
// as repeated, so a document is never sent twice.
// B-101 — the client's own rules first (the backend's `clientFileRules`, the same its email trigger
// reads): "conciliacion e pago 15092026.xlsx" is the bank's file though it shares no word with it.
const clavePorReglas = (nombre, reglas, claves) => {
  const regla = (reglas || []).find((r) => {
    try {
      return new RegExp(r.name, "i").test(nombre) && (r.documents || []).some((d) => claves.includes(d));
    } catch (e) {
      return false;
    }
  });
  return regla ? regla.documents.find((d) => claves.includes(d)) : null;
};

export const asignarArchivos = (archivos, claves, nombres, { fecha, reglas } = {}) => {
  const asignados = {};
  const sinReconocer = [];
  const repetidos = [];
  for (const archivo of Array.from(archivos || [])) {
    const clave = clavePorReglas(archivo.name, reglas, claves) || claveDeArchivo(archivo.name, claves, nombres);
    if (!clave) {
      sinReconocer.push(archivo);
      continue;
    }
    const previo = asignados[clave];
    const delDia = (a) => mencionaFecha(a.name, fecha);
    if (!previo) {
      asignados[clave] = archivo;
    } else if (delDia(archivo) !== delDia(previo)) {
      if (delDia(archivo)) {
        asignados[clave] = archivo;
        repetidos.push(previo);
      } else {
        repetidos.push(archivo);
      }
    } else if ((archivo.lastModified || 0) > (previo.lastModified || 0)) {
      asignados[clave] = archivo;
      repetidos.push(previo);
    } else {
      repetidos.push(archivo);
    }
  }
  return { asignados, sinReconocer, repetidos };
};

// What the person reads after dropping files, beside the slots that filled.
export const avisoDeAsignacion = ({ sinReconocer, repetidos }) => [
  sinReconocer.length
    ? `No se reconoció ${sinReconocer.map((a) => a.name).join(", ")}. Elígelo en su fila.`
    : null,
  repetidos.length
    ? `Se ignoró ${repetidos.map((a) => a.name).join(", ")}: ya había otro archivo para ese documento.`
    : null,
].filter(Boolean);

// --- A folder of reports (B-70) ----------------------------------------------------------------
//
// The day's reports usually sit together in one folder, and a folder is what people drop. The
// browser hands a dropped folder over as an entry to walk, not as files, and a folder chosen with
// the picker brings everything in it — the macro's other files, Excel's `~$` lock files, a
// `desktop.ini`. Only the files of the slots' format go on; anything else would only come back as
// "no se reconoció".

// Whether a file goes on: of the format `accept` names (its extensions; none named, any), and not
// a lock or system file.
export const admiteArchivo = (nombre, accept) => {
  const base = String(nombre || "").split(/[/\\]/).pop();
  if (!base || base.startsWith("~$") || base.startsWith(".") || /^(desktop\.ini|thumbs\.db)$/i.test(base)) return false;
  const extensiones = String(accept || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.startsWith("."));
  return !extensiones.length || extensiones.some((e) => base.toLowerCase().endsWith(e));
};

// Every file under dropped entries (`DataTransferItem.webkitGetAsEntry()`), folders walked to the
// bottom. A directory reader answers in batches, so it is read until it answers none.
export const archivosDeEntradas = async (entradas) => {
  const archivos = [];
  const visitar = async (entrada) => {
    if (!entrada) return;
    if (entrada.isFile) {
      const archivo = await new Promise((resolve, reject) => entrada.file(resolve, reject));
      // B-101 — where it was: the folder can say which office a file is from.
      try {
        Object.defineProperty(archivo, "rutaRelativa", { value: String(entrada.fullPath || archivo.name).replace(/^\//, "") });
      } catch (e) {
        // A File that will not take the property keeps its name as its path.
      }
      archivos.push(archivo);
      return;
    }
    if (!entrada.isDirectory) return;
    const lector = entrada.createReader();
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const lote = await new Promise((resolve, reject) => lector.readEntries(resolve, reject));
      if (!lote.length) break;
      // eslint-disable-next-line no-await-in-loop
      for (const hija of lote) await visitar(hija);
    }
  };
  for (const entrada of Array.from(entradas || [])) {
    // eslint-disable-next-line no-await-in-loop
    await visitar(entrada);
  }
  return archivos;
};

// B-101 — a file's path within what was dropped or chosen: the folder walked, the picker's
// `webkitRelativePath`, or just its name.
export const rutaDeArchivo = (archivo) => archivo?.rutaRelativa || archivo?.webkitRelativePath || archivo?.name || "";
